import { Box } from '@mui/material';

import { CategoryGrid } from './CategoryGrid';
import { ContractHeader } from './ContractHeader';
import { useFinancials } from './editor';
import { GuidedEntryProvider, useGuidedEntry } from './flow';
import { OverBudgetBanner } from './OverBudgetBanner';
import { TotalsRow } from './TotalsRow';

/**
 * The main contract panel: header, totals strip, over-budget banner and the
 * category bento — wrapped in the guided-entry provider so an empty contract
 * walks amount → cards → hours (BR-21). Clicking outside the walk's inputs
 * ends it, keeping whatever was committed.
 */
const ContractPanelBody = () => {
  const guided = useGuidedEntry();
  const { state } = useFinancials();

  return (
    <Box
      data-automation-id="contract-panel"
      onClick={(event) => {
        if (!guided.flow) return;
        /*
          An untouched contract keeps its walk through incidental clicks. The
          walk starts itself now (there is no launchpad button to restart it),
          so treating every click in the panel as an exit made it one-way: set
          the billing method or open the rate card first — both reasonable
          opening moves — and guided entry was gone for that contract. Escape
          still leaves, and so does committing a value.

          Once the contract has a budget the walk is a refinement rather than
          the way in, and clicking away ends it as before, keeping whatever was
          committed.
        */
        if (state.document.revenue === 0) return;
        const target = event.target as HTMLElement;
        if (target.closest('input,[data-automation-id="flow-entry"]')) return;
        guided.exit();
      }}
      sx={{
        flex: 1,
        minWidth: 0,
        bgcolor: 'var(--bg-panel)',
        borderRadius: 'var(--border-radius-x-large)',
        border: 'var(--border-width-hairline) solid var(--border-subtle)',
        /*
          Stretches to the row, like the contracts rail beside it and like
          Planning's populated card. It used to be `alignSelf: 'flex-start'`,
          which sized it to its own content — so the rail (`alignSelf: stretch`)
          and this card were only ever the same height by coincidence, and a
          short contract left a full-height rail standing beside a stub of a
          card. Scrolling therefore moves inside this card rather than taking
          the pair with it, exactly as `plan-scroller` does.
        */
        minHeight: 0,
        overflow: 'auto',
      }}
    >
      <ContractHeader />
      <Box sx={{ px: 2.5, py: 2 }}>
        <TotalsRow />
        <OverBudgetBanner />
        <CategoryGrid />
      </Box>
    </Box>
  );
};

export const ContractPanel = () => {
  const { state } = useFinancials();
  return (
    /*
      Keyed by contract AND billing method, because the walk seeds itself at
      mount and both of those change what it should ask.

      Contract: the walk belongs to one contract, so selecting a second empty
      one must open in its own rather than inherit the first's finished walk.

      Method: fixed price asks for an amount, T&M asks for hours. Switching the
      method on an empty contract re-asks the right question — which is also
      what makes switching it recoverable at all, since the panel's
      click-outside handler below treats the method dropdown as a click away
      from the walk and ends it.
    */
    <GuidedEntryProvider key={`${state.contractId}:${state.type}`}>
      <ContractPanelBody />
    </GuidedEntryProvider>
  );
};
